import * as React from 'react';
import { Button, Checkbox, Dropdown, Input, MessageBar, MessageBarBody, Option, Textarea } from '@fluentui/react-components';
import { CalendarAddRegular, CheckmarkRegular, MailRegular, SaveRegular } from '@fluentui/react-icons';
import { EVENT_LABELS, RETIRED_EVENTS, WEEKEND_NOTE, type EventKind } from '../../api/src/programs';
import { takeResume } from '../auth';
import { errorText, useApp } from '../app/context';
import { PRODUCT_NAME } from '../app/product';
import { uploadSmallFile } from '../graphx';
import { createAllDayEvent, createDraft } from '../outlook';
import { daysBetween, dueWords, eventLabel, type IEventInput, type IEventView } from '../records/events';
import { DeadlinePreview, EventDates, eventInputReady } from '../components/EventEntry';
import { type IItemView, localToday } from '../records/model';
import { requestHtml, type ITabData } from '../records/requests';
import { recordEvent } from '../records/recordEvent';
import UploadLinkCard from '../components/UploadLinkCard';

export type { ITabData };


/** What the client still owes: a page in the file's folder, an Outlook draft, and marking them requested. */
export function RequestsTab(props: { data: ITabData; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const d: ITabData = props.data;
  const open: IItemView[] = d.items.filter(i => i.status === 'Needed' || i.status === 'Requested');
  const [chosen, setChosen] = React.useState<string[]>(() => open.map(i => i.key));
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [done, setDone] = React.useState<string | undefined>(undefined);
  const selected: IItemView[] = open.filter(i => chosen.indexOf(i.key) !== -1);
  const today: string = localToday();
  const firm: string = app.workspace.siteName || '';
  const subject: string = `Documents we still need for your ${d.program?.short ?? 'application'}`;

  const run = async (what: string, step: () => Promise<void>): Promise<void> => {
    setWorking(what);
    setError(undefined);
    setDone(undefined);
    try {
      await step();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(undefined);
    }
  };

  const save = (): Promise<void> => run('Saving the page…', async () => {
    if (!d.application.folderId) {
      throw new Error('This application has no folder.');
    }
    const name: string = `Document request ${today} ${new Date().toTimeString().slice(0, 5).replace(':', '')}.html`;
    await uploadSmallFile(app.graph, d.set.library.driveId, d.application.folderId, name, requestHtml(d, selected, firm, today), 'text/html');
    setDone(`Saved "${name}" in the application's folder. Open it there to print it or save it as a PDF.`);
  });

  const draft = React.useCallback((items: IItemView[]): Promise<void> => run('Creating the Outlook draft…', async () => {
    const html: string = requestHtml(d, items, firm, today).replace(/^[\s\S]*<body>/, '').replace(/<\/body>[\s\S]*$/, '');
    const link: string = await createDraft(d.client?.email, subject, html, { kind: 'requestDraft', application: d.application.key, items: items.map(i => i.key) });
    window.open(link, '_blank', 'noopener');
    setDone(`The draft is in your Outlook Drafts${d.client?.email ? ` to ${d.client.email}` : ''}. Read it and send it yourself.`);
  }), [d, firm, today, subject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back from Microsoft's permission page: finish the draft that was being made.
  React.useEffect(() => {
    const r = takeResume('requestDraft');
    if (r && r.application === d.application.key) {
      const keys: string[] = Array.isArray(r.items) ? r.items as string[] : [];
      void draft(open.filter(i => keys.indexOf(i.key) !== -1));
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRequested = (): Promise<void> => run('Marking them requested…', async () => {
    const batch = d.set.writer.batch();
    for (const i of selected.filter(x => x.status === 'Needed')) {
      await batch.update(d.set.checklist, i.rec, { Status: 'Requested', ChangedBy: app.account.username, ChangedOn: new Date().toISOString() }, i.label, `Marked "${i.label}" requested`);
    }
    props.onChanged();
  });

  if (!open.length) {
    return <div className="card"><span style={{ fontSize: 15 }}>Nothing is needed from the client right now.</span></div>;
  }
  return (
    <>
    <UploadLinkCard data={d} onChanged={props.onChanged} />
    <section className="card" style={{ gap: 12 }}>
      <h2>Other ways to ask</h2>
      <span className="small" style={{ fontSize: 14 }}>Choose what to ask for. Save it as a page in the file's folder (to print or save as PDF), or open it as a draft email in your Outlook. Nothing is sent for you.</span>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {done && <MessageBar intent="success"><MessageBarBody>{done}</MessageBarBody></MessageBar>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Checkbox label={<strong>All ({open.length})</strong>} checked={selected.length === open.length ? true : selected.length ? 'mixed' : false} onChange={(_, x) => setChosen(x.checked ? open.map(i => i.key) : [])} />
        {open.map(i => (
          <Checkbox key={i.key} checked={chosen.indexOf(i.key) !== -1} onChange={(_, x) => setChosen(list => (x.checked ? [...list, i.key] : list.filter(k => k !== i.key)))}
            label={<span>{i.label}{i.form ? <span className="small"> · {i.form}</span> : null}{i.status === 'Requested' ? <span className="small"> · requested{i.changedOn ? ` ${daysBetween(i.changedOn.slice(0, 10), localToday())} days ago` : ''}</span> : null}</span>} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button icon={<SaveRegular />} disabled={!!working || !selected.length} onClick={() => { void save(); }}>Save as a page in the folder</Button>
        <Button icon={<MailRegular />} disabled={!!working || !selected.length} onClick={() => { void draft(selected); }}>Open as an Outlook draft</Button>
        <Button icon={<CheckmarkRegular />} disabled={!!working || !selected.some(i => i.status === 'Needed')} onClick={() => { void markRequested(); }}>Mark them requested</Button>
      </div>
      {working && <span className="small">{working}</span>}
      {!d.client?.email && <span className="small">The client has no email address in their record, so the draft will have no recipient.</span>}
    </section>
    </>
  );
}

/** What IRCC (or the IRB, or the court) sent, and the deadlines that follow, calculated for a person to check. */
export function DeadlinesTab(props: { data: ITabData; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const d: ITabData = props.data;
  const today: string = localToday();
  const [kind, setKind] = React.useState<EventKind | ''>('');
  const [input, setInput] = React.useState<IEventInput>({ letterDate: today });
  const [note, setNote] = React.useState<string>('');
  const [expires, setExpires] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const run = async (step: () => Promise<void>): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      await step();
      props.onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const add = (): Promise<void> => run(async () => {
    if (!kind || !d.program) {
      return;
    }
    await recordEvent(d.set, d.set.writer.batch(), d.application, d.program, kind, input, note.trim() || undefined);
    setKind('');
    setNote('');
    setInput({ letterDate: today });
  });

  // Temporary residence: the permit's expiry on the file, and the deadline to apply before it.
  const saveExpiry = (): Promise<void> => run(async () => {
    if (!d.program) {
      return;
    }
    const batch = d.set.writer.batch();
    await batch.update(d.set.applications, d.application.rec, { PermitExpires: expires }, d.application.title, `Permit or status on ${d.application.title} expires on ${expires}`);
    await recordEvent(d.set, batch, d.application, d.program, 'permitExpiry', { letterDate: expires }, undefined);
    setExpires('');
  });

  const confirm = (e: IEventView, due: string): Promise<void> => run(async () => {
    await d.set.writer.batch().update(d.set.events, e.rec, { Due: due, ConfirmedBy: app.account.username, ConfirmedOn: new Date().toISOString() }, e.title, `${app.account.username} checked the deadline "${e.title}" (${due}) against the letter and confirmed it`);
  });
  const markDone = (e: IEventView): Promise<void> => run(async () => {
    await d.set.writer.batch().update(d.set.events, e.rec, { DoneBy: app.account.username, DoneOn: new Date().toISOString() }, e.title, `Marked "${e.title}" done`);
  });
  const toCalendar = React.useCallback((e: IEventView): Promise<void> => run(async () => {
    const id: string = await createAllDayEvent(`${e.title}: ${d.client?.name ?? d.application.title}`, e.due!, `${d.application.title}. ${e.ruleSource ?? ''} Added from ${PRODUCT_NAME}.`, { kind: 'calendarEvent', event: e.key });
    await d.set.events.update(e.rec.id, { CalendarEventId: id });
    app.toast({ text: `Added to your Outlook calendar on ${e.due}, with a reminder a week before.` });
  }), [d]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back from Microsoft's permission page: add the event that was being added.
  React.useEffect(() => {
    const r = takeResume('calendarEvent');
    const e: IEventView | undefined = r ? d.events.filter(x => x.key === r.event)[0] : undefined;
    if (e) {
      void toCalendar(e);
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deadlines: IEventView[] = d.events.filter(e => e.kind === 'deadline' || e.kind === 'instalment').sort((a, b) => (a.due ?? '').localeCompare(b.due ?? ''));
  const firstUnchecked: IEventView | undefined = deadlines.filter(e => e.kind === 'deadline' && !e.doneOn && !e.confirmedOn)[0];
  const happened: IEventView[] = d.events.filter(e => e.kind === 'event').sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));

  return (
    <>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {d.program?.maintainedStatus && (
        <section className="card" style={{ gap: 10 }}>
          <h2>Permit or status expires on</h2>
          <span className="small" style={{ fontSize: 14 }}>
            {d.application.permitExpires ? `${d.application.permitExpires}, from the permit. ` : 'Not entered yet. '}
            Applying before this date keeps the person's status while IRCC decides. Saving it adds "Apply before this date to keep status" on that date, and a warning 30 days before.
          </span>
          <div className="form-grid">
            <label>{d.application.permitExpires ? 'New expiry date' : 'Expires on'}<Input type="date" value={expires} onChange={(_, x) => setExpires(x.value)} /></label>
          </div>
          <Button disabled={working || !expires} onClick={() => { void saveExpiry(); }} style={{ alignSelf: 'flex-start' }}>Save the expiry date</Button>
        </section>
      )}

      <section className="card" style={{ gap: 12 }}>
        <h2>Record a letter or event</h2>
        <div className="form-grid">
          <label>What happened
            <Dropdown placeholder="Choose" value={kind ? eventLabel(kind) : ''} selectedOptions={kind ? [kind] : []} onOptionSelect={(_, x) => setKind((x.optionValue ?? '') as EventKind)}>
              {(Object.keys(EVENT_LABELS) as EventKind[]).filter(k => RETIRED_EVENTS.indexOf(k) === -1).map(k => <Option key={k} value={k}>{EVENT_LABELS[k]}</Option>)}
            </Dropdown>
          </label>
          {kind && <EventDates kind={kind} input={input} onChange={setInput} />}
        </div>
        {kind && <Textarea placeholder="Note (optional)" value={note} onChange={(_, x) => setNote(x.value)} />}
        {kind && d.program && <DeadlinePreview program={d.program} kind={kind} input={input} onChange={setInput} />}
        <Button appearance={firstUnchecked ? 'secondary' : 'primary'} disabled={working || !kind || !eventInputReady(kind, input, today)} onClick={() => { void add(); }} style={{ alignSelf: 'flex-start' }}>Record it</Button>
      </section>

      <section className="card" style={{ gap: 0 }}>
        <h2 style={{ marginBottom: 6 }}>Deadlines</h2>
        {deadlines.length === 0 && <span className="small" style={{ fontSize: 15 }}>No deadlines yet. They're added when you record a letter above.</span>}
        {deadlines.map(e => <DeadlineRow key={e.key} e={e} first={e === firstUnchecked} today={today} working={working} onConfirm={due => { void confirm(e, due); }} onDone={() => { void markDone(e); }} onCalendar={() => { void toCalendar(e); }} />)}
      </section>

      <section className="card" style={{ gap: 6 }}>
        <h2>What happened</h2>
        {happened.length === 0 && <span className="small" style={{ fontSize: 15 }}>Nothing recorded yet.</span>}
        <ul className="steps-list" style={{ fontSize: 14 }}>
          {happened.map(e => <li key={e.key}>{e.eventDate}: {e.title}{e.note ? ` (${e.note})` : ''}</li>)}
        </ul>
      </section>
    </>
  );
}

function DeadlineRow(props: { e: IEventView; today: string; working: boolean; first?: boolean; onConfirm: (due: string) => void; onDone: () => void; onCalendar: () => void }): React.ReactElement {
  const e: IEventView = props.e;
  const [due, setDue] = React.useState<string>(e.due ?? '');
  const late: boolean = !e.doneOn && !!e.due && e.due < props.today;
  return (
    <div className="item-row">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontWeight: 600, color: late ? 'var(--orange-text)' : undefined }}>{e.title}: {e.due}{!e.doneOn && e.due ? ` · ${dueWords(e.due, props.today)}` : ''}</span>
        <span className="small">
          {e.doneOn ? `Done by ${e.doneBy}, ${new Date(e.doneOn).toLocaleDateString()}`
            : e.kind === 'instalment' ? 'Fee instalment (a reminder; your accounting is where it\'s billed)'
              : e.confirmedOn ? `Confirmed by ${e.confirmedBy}, ${new Date(e.confirmedOn).toLocaleDateString()}` : <span className="needs-you">Calculated, please check against the letter and confirm</span>}
        </span>
        {e.ruleSource && <span className="small">{e.ruleSource}</span>}
        {e.kind === 'deadline' && !e.doneOn && <span className="small">{WEEKEND_NOTE}</span>}
        {e.calendarEventId && <span className="small">In your Outlook calendar.</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {!e.doneOn && e.kind === 'deadline' && !e.confirmedOn && (
          <>
            <Input type="date" aria-label="Deadline" value={due} onChange={(_, x) => setDue(x.value)} />
            <Button size="small" appearance={props.first ? 'primary' : 'secondary'} disabled={props.working || !due} onClick={() => props.onConfirm(due)}>I checked this date against the letter</Button>
            <span className="small" style={{ maxWidth: 220 }}>The firm's representative is responsible for every deadline.</span>
          </>
        )}
        {!e.doneOn && (e.confirmedOn || e.kind === 'instalment') && !e.calendarEventId && <Button size="small" icon={<CalendarAddRegular />} disabled={props.working} onClick={props.onCalendar}>Add to my calendar</Button>}
        {!e.doneOn && <Button size="small" icon={<CheckmarkRegular />} disabled={props.working} onClick={props.onDone}>{e.kind === 'instalment' ? 'Billed' : 'Done'}</Button>}
      </div>
    </div>
  );
}
